import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import { uniqBy } from 'lodash';
import callApi from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';

// Actions
export const FETCH_PATIENT_MESSAGE_TEMPLATES = 'FETCH_PATIENT_MESSAGE_TEMPLATES';
export const FETCH_PATIENT_MESSAGE_TEMPLATES_SUCCESS = 'FETCH_PATIENT_MESSAGE_TEMPLATES_SUCCESS';
export const FETCH_PATIENT_MESSAGE_TEMPLATES_FAIL = 'FETCH_PATIENT_MESSAGE_TEMPLATES_FAIL';
export const CLEAR_PATIENT_MESSAGE_TEMPLATES = 'CLEAR_PATIENT_MESSAGE_TEMPLATES';
export const CREATE_PATIENT_MESSAGE_TEMPLATE = 'CREATE_PATIENT_MESSAGE_TEMPLATE';
export const CREATE_PATIENT_MESSAGE_TEMPLATE_SUCCESS = 'CREATE_PATIENT_MESSAGE_TEMPLATE_SUCCESS';
export const CREATE_PATIENT_MESSAGE_TEMPLATE_FAIL = 'CREATE_PATIENT_MESSAGE_TEMPLATE_FAIL';
export const UPDATE_PATIENT_MESSAGE_TEMPLATE = 'UPDATE_PATIENT_MESSAGE_TEMPLATE';
export const UPDATE_PATIENT_MESSAGE_TEMPLATE_SUCCESS = 'UPDATE_PATIENT_MESSAGE_TEMPLATE_SUCCESS';
export const UPDATE_PATIENT_MESSAGE_TEMPLATE_FAIL = 'UPDATE_PATIENT_MESSAGE_TEMPLATE_FAIL';
export const DELETE_PATIENT_MESSAGE_TEMPLATE = 'DELETE_PATIENT_MESSAGE_TEMPLATE';
export const DELETE_PATIENT_MESSAGE_TEMPLATE_SUCCESS = 'DELETE_PATIENT_MESSAGE_TEMPLATE_SUCCESS';
export const DELETE_PATIENT_MESSAGE_TEMPLATE_FAIL = 'DELETE_PATIENT_MESSAGE_TEMPLATE_FAIL';

// Reducer
export default function patientsMessagesTemplatesReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_PATIENT_MESSAGE_TEMPLATES:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_PATIENT_MESSAGE_TEMPLATES_SUCCESS:
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: uniqBy(newData, 'ID'),
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case FETCH_PATIENT_MESSAGE_TEMPLATES_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_PATIENT_MESSAGE_TEMPLATES:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case CREATE_PATIENT_MESSAGE_TEMPLATE_SUCCESS: {
      const newData = [
        ...state.data.slice(0, state.data.length),
        payload,
      ];
      return {
        ...state,
        count: state.count + 1,
        data: newData,
      };
    }
    case UPDATE_PATIENT_MESSAGE_TEMPLATE_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_PATIENT_MESSAGE_TEMPLATE_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchPatientMessageTemplates(payload) {
  return { type: FETCH_PATIENT_MESSAGE_TEMPLATES, payload };
}

export function fetchPatientMessageTemplatesSuccess(payload) {
  return { type: FETCH_PATIENT_MESSAGE_TEMPLATES_SUCCESS, payload };
}

export function fetchPatientMessageTemplatesFail(payload) {
  return { type: FETCH_PATIENT_MESSAGE_TEMPLATES_FAIL, payload };
}

export function clearPatientMessageTemplates(payload) {
  return { type: CLEAR_PATIENT_MESSAGE_TEMPLATES, payload };
}

export function createPatientMessageTemplate(payload) {
  return { type: CREATE_PATIENT_MESSAGE_TEMPLATE, payload };
}

export function createPatientMessageTemplateSuccess(payload) {
  return { type: CREATE_PATIENT_MESSAGE_TEMPLATE_SUCCESS, payload };
}

export function createPatientMessageTemplateFail(payload) {
  return { type: CREATE_PATIENT_MESSAGE_TEMPLATE_FAIL, payload };
}

export function updatePatientMessageTemplate(payload) {
  return { type: UPDATE_PATIENT_MESSAGE_TEMPLATE, payload };
}

export function updatePatientMessageTemplateSuccess(payload) {
  return { type: UPDATE_PATIENT_MESSAGE_TEMPLATE_SUCCESS, payload };
}

export function updatePatientMessageTemplateFail(payload) {
  return { type: UPDATE_PATIENT_MESSAGE_TEMPLATE_FAIL, payload };
}

export function deletePatientMessageTemplate(payload) {
  return { type: DELETE_PATIENT_MESSAGE_TEMPLATE, payload };
}

export function deletePatientMessageTemplateSuccess(payload) {
  return { type: DELETE_PATIENT_MESSAGE_TEMPLATE_SUCCESS, payload };
}

export function deletePatientMessageTemplateFail(payload) {
  return { type: DELETE_PATIENT_MESSAGE_TEMPLATE_FAIL, payload };
}

// Requests
export function requestFetchPatientMessageTemplates(payload) {
  return payload
    ? callApi(`patient/sms_template/?limit=${payload.limit}&offset=${payload.offset}`)
    : callApi('patient/sms_template/');
}

export function requestSearchPatientMessageTemplates({ limit, offset, filters }) {
  return callApi('patient/sms_template/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
}

export function requestCreatePatientMessageTemplate(payload) {
  return callApi('patient/sms_template/', 'POST', payload);
}

export function requestUpdatePatientMessageTemplate(payload) {
  return callApi('patient/sms_template/', 'PATCH', payload);
}

export function requestDeletePatientMessageTemplate(payload) {
  return callApi(`patient/sms_template/?id=${payload}`, 'DELETE');
}

// Sagas
function* fetchPatientMessageTemplatesSaga(action) {
  const { triggerLoading, filters } = action.payload;
  try {
    const res = filters
      ? yield call(requestSearchPatientMessageTemplates, action.payload)
      : yield call(requestFetchPatientMessageTemplates, action.payload);
    if (res.count === 0) {
      yield put(fetchPatientMessageTemplatesSuccess({
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    } else if (action.payload.push) {
      yield put(fetchPatientMessageTemplatesSuccess({
        push: true,
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    } else {
      yield put(fetchPatientMessageTemplatesSuccess({
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    }
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchPatientMessageTemplatesFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createPatientMessageTemplateSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreatePatientMessageTemplate, values);
    yield put(createPatientMessageTemplateSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'SMS template successfully added',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createPatientMessageTemplateFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updatePatientMessageTemplateSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdatePatientMessageTemplate, values);
    yield put(updatePatientMessageTemplateSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'SMS template successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updatePatientMessageTemplateFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deletePatientMessageTemplateSaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeletePatientMessageTemplate, ID);
    yield put(deletePatientMessageTemplateSuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'SMS template successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deletePatientMessageTemplateFail(err.data));
    yield put(addNotification({
      message: 'Failed to delete SMS template',
      variant: 'warning',
    }));
  }
}

export function* patientsMessagesTemplatesSaga() {
  yield takeLatest(FETCH_PATIENT_MESSAGE_TEMPLATES, fetchPatientMessageTemplatesSaga);
  yield takeEvery(CREATE_PATIENT_MESSAGE_TEMPLATE, createPatientMessageTemplateSaga);
  yield takeEvery(UPDATE_PATIENT_MESSAGE_TEMPLATE, updatePatientMessageTemplateSaga);
  yield takeEvery(DELETE_PATIENT_MESSAGE_TEMPLATE, deletePatientMessageTemplateSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/patientsMessagesTemplates.js